<template>
  <div>
    <div v-if="loadFormOffer">
      <div class="form-group row">
        <label class="col-md-4 control-label">{{ $t("models.offer.mode") }}</label>
        <div class="col-md-8">
          <div class="radio radio-success">
            <input type="radio" id="quick_mode" name="quick_mode" :value="'quick_mode'" v-model="offer.mode" aria-invalid="false" :disabled="validateOffer" @change="selectRapidSelection">
            <label for="quick_mode">{{ $t('models.offer.modes.quick_mode') }}</label>
            <input type="radio" id="choice_mode" name="choice_mode" :value="'choice_mode'" v-model="offer.mode" aria-invalid="false" :disabled="validateOffer" @change="selectChoiceSelection">
            <label for="choice_mode">{{ $t('models.offer.modes.choice_mode') }}</label>
          </div>
        </div>
      </div>

      <div class="form-group row required" :class="{ 'form-group-ie': isIE }">
        <label for="replaced_occupation" class="col-md-4 control-label">{{ $t("models.offer.replaced_occupation") }} *</label>
        <div class="col-md-8">
          <multiselect v-model="offer.replaced_occupation"
                        track-by="id"
                        label="name"
                        :options="occupationsOptions"
                        :placeholder="$t('multiselect.placeholder')"
                        :showLabels="false"
                        :allow-empty="false"
                        v-validate="'required'"
                        :disabled="validateOffer"
                        data-vv-name="replaced_occupation">
            <span slot="noResult"> {{ $t("multiselect.no_result") }} </span>
          </multiselect>
          <label v-show="errors.has('replaced_occupation')" id="replaced_occupation-error" class="error" for="replaced_occupation">{{ errors.first('replaced_occupation') }}</label>
        </div>
      </div>

      <div class="form-group row required form-multiselect">
        <label for="searched_occupations" class="col-md-4 control-label">{{ $t("models.offer.searched_occupations") }} *</label>
        <div class="col-md-8">
          <multiselect v-if="!isIE"
                        v-model="offer.searched_occupations"
                        track-by="id"
                        label="name"
                        :placeholder="$t('multiselect.placeholder')"
                        :showLabels="false"
                        :multiple="true"
                        :hide-selected="true"
                        :options="occupationsOptions"
                        v-validate="'required'"
                        :disabled="validateOffer"
                        data-vv-name="searched_occupations">
            <span slot="noResult">{{ $t('OfferForm.no_occupation') }}</span>
          </multiselect>
          <v-select v-else
                    multiple
                    v-model="occupationsSelectedIE"
                    :placeholder="$t('multiselect.placeholder')"
                    v-validate="'required'"
                    data-vv-name="searched_occupations"
                    :disabled="validateOffer"
                    :data-vv-as="$t('models.user.occupations')"
                    :options="occupationsOptionsIE">
          </v-select>
          <label v-show="errors.has('searched_occupations')" id="searched_occupations-error" class="error" for="searched_occupations">{{ errors.first('searched_occupations') }}</label>
        </div>
      </div>

      <div v-if="entityHasHubs" class="form-group row required form-multiselect hub-form">
        <label for="hub" class="col-md-4 control-label">{{ $t("models.offer.hub") }} *</label>
        <div v-if="availableHubs.length > 1" class="col-md-8">
          <multiselect v-model="selectedHub"
                        track-by="id"
                        label="name"
                        :options="availableHubs"
                        @input="updateSelectedHub"
                        :placeholder="$t('multiselect.placeholder')"
                        :showLabels="false"
                        :allow-empty="true"
                        :disabled="validateOffer"
                        data-vv-name="hub">
            <template slot="option" slot-scope="props">
              <span>
                {{ props.option.name }}
                <i v-if="selectedHub && props.option.id === selectedHub.id" class="fa fa-times float-right"></i>
              </span>
            </template>
          </multiselect>
          <label v-show="errors.has('hub')" id="replaced_service-error" class="error" for="hub">{{ errors.first('hub') }}</label>
        </div>
        <div v-else class="col-md-8 p-t-5">
          {{ availableHubs[0] ? availableHubs[0].name : "" }}
        </div>
      </div>

      <div class="form-group row required form-multiselect">
        <label for="replaced_service" class="col-md-4 control-label">{{ $t("models.offer.replaced_service") }} *</label>
        <div v-if="manageableServices.length > 1" class="col-md-8">
          <multiselect v-model="offer.replaced_service"
                        track-by="id"
                        label="name"
                        :options="services"
                        :group-label="entityHasHubs ? 'hub' : ''"
                        :group-values="entityHasHubs ? 'services' : ''"
                        :group-select="false"
                        :placeholder="$t('multiselect.placeholder')"
                        :showLabels="false"
                        :allow-empty="false"
                        v-validate="'required'"
                        data-vv-name="replaced_service"
                        :disabled="validateOffer"
          />
          <label v-show="errors.has('replaced_service')" id="replaced_service-error" class="error" for="replaced_service">{{ errors.first('replaced_service') }}</label>
          <div v-show="!selectedServiceMatchWithHub()" class="alert m-t-15 alert-warning">{{ $t("models.offer.service_dont_match_with_hub") }}</div>
        </div>
        <div v-else class="col-md-8">
          <multiselect v-model="offer.replaced_service" track-by="id" label="name"
                        :options="manageableServices"
                        :showLabels="false" :allow-empty="false"
                        :disabled="true"
                        :placeholder="$t('multiselect.placeholder')"
                        v-validate="'required'" data-vv-name="replaced_service"
          />
        </div>
      </div>

      <div class="form-group row">
        <label class="col-md-4 control-label">{{ $t("models.offer.resource_type") }} *</label>
        <div class="col-md-8">
          <div class="radio radio-success">
            <input type="radio"
                    id="intern"
                    name="openedToCandidatesFrom"
                    :value="'intern'"
                    v-model="openedToCandidatesFrom"
                    :disabled="validateOffer"
                    aria-invalid="false"
                    @change="selectChoiceOpeningInterneService">
            <label for="intern">{{ $t('models.resource_types.intern') }}</label>
            <br/>
            <div v-if="entityHasHubs">
              <input type="radio"
                    id="intern_of_concerned_hub"
                    name="openedToCandidatesFrom"
                    :value="'intern_of_concerned_hub'"
                    v-model="openedToCandidatesFrom"
                    :disabled="validateOffer"
                    aria-invalid="false">
              <label for="intern_of_concerned_hub">{{ $t('models.resource_types.intern_of_concerned_hub') }}</label>
              <br/>
            </div>
            <input type="radio"
                    id="intern_with_proposed_candidates"
                    name="openedToCandidatesFrom"
                    :value="'intern_with_proposed_candidates'"
                    v-model="openedToCandidatesFrom"
                    :disabled="validateOffer"
                    aria-invalid="false"
                    @change="selectChoiceOpeningInterneValide">
            <label for="intern_with_proposed_candidates">{{ $t('models.resource_types.intern_with_proposed_candidates') }}</label>
            <br/>
            <input type="radio"
                    id="extern"
                    name="openedToCandidatesFrom"
                    :value="'extern'"
                    v-model="openedToCandidatesFrom"
                    :disabled="validateOffer"
                    aria-invalid="false"
                    @change="selectChoiceOpeningExterne">
            <label for="extern">{{ $t('models.resource_types.extern') }}</label>
            <br/>
            <input type="radio"
                    id="all"
                    name="openedToCandidatesFrom"
                    :value="'all'"
                    v-model="openedToCandidatesFrom"
                    :disabled="validateOffer"
                    aria-invalid="false"
                    @change="selectChoiceOpeningAll">
            <label for="all">{{ $t('models.resource_types.all') }}</label>
          </div>
        </div>
      </div>

      <div class="form-group offer-periods">
        <label class="control-label">{{ $t("models.offer.period") }} *</label>
        <offer-periods-form :offerPeriods="offer.offer_periods_attributes"
                            :numberOfOfferPeriods="offer.number_of_active_offers"
                            :maxDate="maxDate"
                            :disabledDate="validateOffer"
                            @removeOfferPeriod="removeOfferPeriod"
                            @updateOfferPeriod="updateOfferPeriod" />
        <div v-if="hasMultiplePeriod && !validateOffer" class="">
          <label class="inline-element col-md-4 control-label">{{ $t("models.offer.create_offers_for_each_period") }}</label>
          <div class="inline-element col-md-8">
            <slide-toggle @input="updateCreateOffersByEachPeriod" :stringValues="createOffersByEachPeriodValues" :canEdit="true" />
          </div>
        </div>
      </div>

      <div class="form-group row">
        <label class="col-md-4 control-label">
          {{ $t("models.offer.replacement_motive") }}
          <p class="offer-form-notice">
            {{ $t("models.offer.not_visible_by_candidate") }}
          </p>
        </label>
        <div class="col-md-8">
          <multiselect v-model="replacementMotive"
                        track-by="id"
                        label="name"
                        :options="replacementMotivesOptions"
                        :placeholder="$t('multiselect.placeholder')"
                        :showLabels="false"
                        :allow-empty="true"
                        deselectLabel="X"
                        selectedLabel="X"
                        selectLabel=""
                        data-vv-name="replacement_motive"
                        @input="onChangeReplacementMotive">
            <span slot="noResult">{{ $t("multiselect.no_result") }}</span>
          </multiselect>
        </div>
      </div>

      <div  class="form-group row">
        <label class="col-md-4 control-label">
          {{ $t("models.offer.replacement_motive_detail") }}
          <p class="offer-form-notice">
            {{ $t("models.offer.not_visible_by_candidate") }}
          </p>
        </label>
        <div class="col-md-8">
        <textarea name="replacement_motive_details"
                  v-model="offer.replacement_detail_attributes.details"
                  id="replacement_motive_details"
                  class="form-control"
                  rows="3"></textarea>
        </div>
      </div>

      <div class="form-group row">
        <label class="col-md-4 control-label">{{ $t("models.offer.details") }}</label>
        <div class="col-md-8">
          <textarea v-model="offer.details" class="form-control" rows="3" :disabled="validateOffer"></textarea>
        </div>
      </div>

      <div class="form-group row">
        <label class="col-md-4 control-label">{{ $t("models.offer.person_in_copy") }}</label>
        <div class="col-md-8">
          <multiselect v-model="managersEntity"
                        :options="managersFromEntity"
                        track-by="id"
                        label="full_name"
                        :disabled="validateOffer"
                        :placeholder="$t('multiselect.placeholder')"
                        :showLabels="false"
                        :multiple="true"
                        :hide-selected="true"
                        @input="onChangeManagerEntity">
            <span slot="noResult">{{ $t("multiselect.no_result") }}</span>
            <span slot="maxElements">{{ $t("multiselect.max_options") }}</span>
          </multiselect>
        </div>
      </div>

      <div class="form-group row" v-if='validateOffer'>
        <label class="col-md-4 control-label">
          {{ $t("models.offer.edit_comment") }}
          <p class="offer-form-notice">
            {{ $t("models.offer.not_visible_by_candidate") }}
          </p>
        </label>
        <div class="col-md-8">
          <textarea name="edit_comment"
                  v-model="offer.edit_comment"
                  id="edit_comment"
                  class="form-control"
                  rows="3">
          </textarea>
        </div>
      </div>
    </div>

    <div v-if="showContractFields">
      <h4 class="table-title collapsible" @click="toggleContractFields" ref="collapsible">
        <span>{{ $t('OfferForm.contract_title') }}</span>
      </h4>

      <transition name="slide-fade">
        <div v-show="showContractField">
          <div class="alert contract-information mt-4">
            <p>{{ $t('OfferForm.contract_message') }}</p>
          </div>

          <div class="form-group row">
            <label class="col-md-4 control-label">
              {{ $t("models.offer.replaced_person") }}
              <p class="offer-form-notice">
                {{ $t("models.offer.not_visible_by_candidate") }}
              </p>
            </label>
            <div class="col-md-8">
              <input v-model="offer.replaced_person" class="form-control" type="text" rows="3" :disabled="validateOffer">
            </div>
          </div>

          <div class="form-group row">
            <label class="col-md-4 control-label">
              {{ $t('models.offer.replaced_person_salary_category') }}
              <p class="offer-form-notice">
                {{ $t("models.offer.not_visible_by_candidate") }}
              </p>
            </label>
            <div class="col-md-8">
              <input v-model="offer.replaced_person_salary_category" class="form-control" type="text" rows="3" :disabled="validateOffer">
            </div>
          </div>

          <div class="form-group row">
            <label class="col-md-4 control-label">
              {{ $t('models.offer.replaced_person_salary') }}
              <p class="offer-form-notice">
                {{ $t("models.offer.not_visible_by_candidate") }}
              </p>
            </label>
            <div class="col-md-8">
              <input v-model="offer.salary" class="form-control" type="text" rows="3" :disabled="validateOffer">
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>
<script>
import DateTimePicker from '@/components/pages/DateTimePicker'
import DurationTimeInput from '@/components/timeCodes/DurationTimeInput'
import SlideToggle from '@/components/pages/SlideToggle'
import OfferPeriodsForm from '@/components/offers/OfferPeriodsForm'
import { mapGetters, mapActions } from 'vuex'
import { checkCanEditContract } from '@/helpers/contracts'
import ie_mixin from '@/mixin/ie'
import _ from 'lodash';
import AtInternetManager from '@/helpers/at_internet/manage_requests'

export default {
  name: 'OfferInformationsForm',
  props: ['offer', 'selectedEntityId'],
  mixins: [ie_mixin],
  data: function(){
    return {
      createOffersByEachPeriodValues: {
        on: true,
        off: false
      },
      openedToCandidatesFrom: 'intern',
      selectedHub: null,
      replacementMotive: '',
      managersEntity: [],
      managersToAddInCopy: [],
      showContractField: false
    }
  },
  components: { DateTimePicker, DurationTimeInput, OfferPeriodsForm, SlideToggle },
  computed: {
    ...mapGetters({
      manageableServices: 'users/manageableServices',
      currentUser: 'auth/currentUser',
      occupationsOptions: 'users/manageableOccupations',
      replacementMotivesOptions: 'replacementMotives/replacementMotives',
      managersInCopy: 'offers/managersInCopy',
      managersFromEntity: 'users/managersFromEntity',
      loadFormOffer: 'offers/loadFormOffer'
    }),
    validateOffer() {
      return this.offer.status == "validated" || this.offer.is_expired
    },
    entityHasHubs() {
      return this.offer.entity.hubs && this.offer.entity.hubs.length > 1
    },
    availableHubs() {
      if(this.selectedEntityId) {
        return this.currentUser.hubs.filter((hub) => hub.entity_id == this.selectedEntityId);
      }
      return this.currentUser.hubs;
    },
    services() {
      if(!this.entityHasHubs) {
        return this.manageableServices;
      }
      return this.buildServicesListGroupedByHubs();
    },
    maxDate() {
      var dates =  this.offer.offer_periods_attributes.map(period => period.start_at)
      var momentDates = dates.map(date => moment(date))
      return moment.max(momentDates)
    },
    hasMultiplePeriod(){
      return this.offer.offer_periods_attributes.length > 1;
    },
    showContractFields() {
      return checkCanEditContract(this.currentUser, this.offer.entity.id) &&
        (this.offer.resource_type === "extern" || this.offer.resource_type === "all")
    },
  },
  watch: {
    manageableServices() {
      if (this.manageableServices.length == 1) {
        this.offer.replaced_service = this.manageableServices[0]
      } else if (this.manageableServices.length == 0) {
        this.offer.replaced_service = []
      }
    },
    occupationsSelectedIE: function (values) {
      var selected = []
      values.map(item => selected.push(this.searchInArray(item, this.occupationsOptions)))
      this.offer.searched_occupations = selected
    },
    openedToCandidatesFrom(newValue, oldValue){
      this.offer.resource_type = newValue == 'intern_with_proposed_candidates' || newValue ==  'intern_of_concerned_hub' ? 'intern' : this.openedToCandidatesFrom;
      this.offer.include_proposed_candidates = this.openedToCandidatesFrom == 'intern' || this.openedToCandidatesFrom == 'intern_of_concerned_hub' ? false : true;
      if(this.offer.entity.hubs.length > 1){
        this.offer.include_hub_services = this.openedToCandidatesFrom != 'all' && this.openedToCandidatesFrom != 'intern_of_concerned_hub' ? false : true;
      }
      if(newValue == 'intern' || newValue =='intern_with_proposed_candidates' || newValue =='intern_of_concerned_hub') {
        this.resetContractInformationsFields()
      }
    },
    replacementMotive(newValue){
      this.$emit('updateReplacementMotive', newValue)
    },
    managersEntity(value) {
      this.managersToAddInCopy = value
      this.$emit('managersToAddInCopy', this.managersToAddInCopy)
    },
  },
  methods: {
    ...mapActions({
      initManageableOptionsAction: 'users/initManageableOptions',
      getOffer: 'offers/getOffer',
      getManagerInCopy: 'offers/getManagerInCopy',
      setCandidatesListPageAction: 'offers/setCandidatesListPage',
      initReplacementMotivesAction: 'replacementMotives/initReplacementMotives',
    }),
    updateSelectedHub: function(value){
      if (value != null) {
        this.initManageableOptionsAction({ entityId: this.offer.entity.id, hubIds: [this.selectedHub.id] });
      } else {
        this.initManageableOptionsAction({ entityId: this.offer.entity.id });
      }
    },
    buildServicesListGroupedByHubs() {
      return this.availableHubs.map(hub => {
        return {
          hub: hub.name,
          services: this.manageableServices.filter(service => service.hub_id == hub.id)
        }
      });
    },
    selectedServiceMatchWithHub() {
      const replacedService = this.offer.replaced_service;
      return !replacedService.hub_id ||
             !this.selectedHub ||
             replacedService.hub_id == this.selectedHub.id
    },
    initOfferCustomParameters: function() {
      if(this.offer.resource_type == "intern" &&
         this.offer.include_hub_services == true){
        this.openedToCandidatesFrom = "intern_of_concerned_hub";
      }else if(this.offer.resource_type == "intern" &&
         this.offer.include_proposed_candidates == false){
        this.openedToCandidatesFrom = "intern";
      }else if(this.offer.resource_type == "intern"){
        this.openedToCandidatesFrom = "intern_with_proposed_candidates";
      }else{
        this.openedToCandidatesFrom = this.offer.resource_type;
      }
      this.replacementMotive = {
        id: this.offer.replacement_detail_attributes.replacement_motive_id,
        name: this.offer.replacement_detail_attributes.replacement_motive_name,
        details: this.offer.replacement_detail_attributes.details
      }
    },
    resetContractInformationsFields() {
      this.offer.salary = null
      this.offer.replaced_person_salary_category = null
      this.offer.replaced_person = null
    },
    removeOfferPeriod(index){
      this.$emit('removeOfferPeriod', index)
    },
    updateOfferPeriod(index, { time_code_id, time_code_name, start_at, end_at, effective_work_minutes }) {
      let offerPeriod = this.offer.offer_periods_attributes[index]
      offerPeriod.effective_work_minutes  = effective_work_minutes
      offerPeriod.time_code_id            = time_code_id
      offerPeriod.time_code_name          = time_code_name
      offerPeriod.start_at                = moment(start_at)
      offerPeriod.end_at                  = moment(end_at)
    },
    updateCreateOffersByEachPeriod: function(value) {
      this.createOffersByEachPeriod = value
      this.$emit("createOffersByEachPeriod", this.createOffersByEachPeriod)
    },
    toggleContractFields() {
      this.showContractField = !this.showContractField
      this.$refs.collapsible.classList.toggle('opened')
    },
    onChangeReplacementMotive(){
      AtInternetManager.sendReasonForReplacementEvent()
    },
    selectChoiceOpeningInterneService() {
      AtInternetManager.sendInternalDepartementOpeningEvent()
    },
    selectChoiceOpeningInterneValide() {
      AtInternetManager.sendInternalDepartementOpeningValidateEvent()
    },
    selectChoiceOpeningExterne() {
      AtInternetManager.sendExternalOpeningEvent()
    },
    selectChoiceOpeningAll() {
      AtInternetManager.sendOpeningForAllEvent()
    },
    selectRapidSelection() {
      AtInternetManager.sendFastModeRequestEvent()
    },
    selectChoiceSelection() {
      AtInternetManager.sendChoiceModeRequestEvent()
    },
    onChangeManagerEntity(){
      if(this.managersEntity.length >= 1){
        AtInternetManager.sendNumberCopyPersonEvent()
      }
    },
  },
  mounted: async function() {

    this.initOfferCustomParameters();
    this.initReplacementMotivesAction();
    this.initOfferCustomParameters();

    await this.initManageableOptionsAction({ entityId: this.offer.entity.id });

    if(this.isIE) {
      this.occupationsSelectedIE = [];
      if (this.offerId) {
        await this.getOffer(this.offerId);
        this.occupationsSelectedIE = this.offer.searched_occupations.map(item => item['name'])
      } else if (this.offer) {
        this.occupationsSelectedIE = this.offer.searched_occupations.map(item => item['name'])
      } else {
        this.occupationsSelectedIE = []
      }
    }
    if(this.offer.id) {
      await this.getManagerInCopy({id: this.offer.id});
      this.managersEntity = this.managersInCopy;
    }
  }
}
</script>
<style lang="scss" scoped>
  .table-periods {
    margin: 0 auto;
  }

  .offer-periods{
    display: block;
  }
  .contract-information {
    background-color: #eeefef;
    border-radius: 8px;
  }

  @media screen and (max-width: 991px) {
    .top-fixed {
      top: 50px;
    }
  }

  @media screen and (min-width: 992px) {
    .top-fixed {
      top: 94px;
    }
  }

  .potential-candidates {
    text-align: center;
    position: fixed;
    left: 0;
    z-index: 100;
    width: 340px;
    height: 40px;
    margin: 0 0 0 -200px;
    left: 50%;
    color: #11705c;
  }

  .label-potential-candidates {
    cursor: pointer;
    width: 80%;
  }

  #select-entity {
    border-bottom: none;
  }

  @media (max-width: 767px) {
    .btn {
      width: 100%;
    }
    .btn-cons {
      margin-right: 0;
    }
    .col-md-4, .col-md-8 {
      padding-right: 0;
      padding-left: 0;
    }
  }

  a.disabled {
    pointer-events: none;
    cursor: default;
  }

  .hub-form {
    border-bottom: 0px;
  }
  .table-title {
    span {
      padding-left: 0;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/offers/OfferInformationsForm.vue